// LIFTED from ptc-tax-lifecycle web-app/src/components/listFilters/SearchableMultiSelect.tsx @ 7af60ebb (C86, filter contract v3 §16.8, 2026-11-02),
// verbatim except ONE adaptation, the fleet copy's first change: the `labels` prop (2026-11-02), because the reference
// rendered "Select all" / "Clear" / "No matches" / "… value" / "… search" in English only and the fleet's apps are
// bilingual (core's i18n sweep refused it). English stays the default; every caller passes its translated labels.
// THE FLEET'S SHARED COPY: statetax and supervision copy FROM HERE (ptc-core), never a third copy; tax-lifecycle re-copies.
import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Badge, Button, Checkbox, Field, Input, Switch, makeStyles, tokens } from '@fluentui/react-components';
import { Dismiss12Regular } from '@fluentui/react-icons';

// The fleet's ONE lookup-column filter control (fleet filter contract v3 §16.8 / case C86,
// Christian's ruling 2026-11-02): a search box over the options, each with a facet count, the
// selection as removable chips, Select all / Clear, an optional "Exclude these" toggle and an
// optional "(has no value)" row -- never an operator dropdown over a single-value picker.
//
// Deliberately PRESENTATIONAL and independent of the `filter.<col>.<op>` record shape: this file
// knows nothing about filters. ColumnHeaderFilter.tsx is the thin adapter that translates to/from
// that record (via picklistSelection.ts); the Preparer Roster's multi-select preparer picker uses
// this SAME component directly, with neither `noValueOption` nor `excludeToggle`, because a plain
// picker has no "no value"/"exclude" concept -- Christian's own wording was "via the same combobox".
//
// Fluent v9 gotcha (same one columnFilterOps.tsx documents): no bare aria-label on a component that
// embeds further Fluent controls without checking first for a render-loop; this one has none of
// that hazard (plain Input/Checkbox/Switch, no controlled Dropdown), so a normal aria-label is fine.

export interface SearchableMultiSelectOption {
  value: string;
  label: string;
}

export interface SearchableMultiSelectProps {
  /** Accessible label stem, e.g. "Preparer" -- used to build the search box's aria-label. */
  label: string;
  options: SearchableMultiSelectOption[];
  /** Selected option VALUES (never includes a no-value sentinel -- that's `noValueOption` below). */
  selected: string[];
  onChange: (next: string[]) => void;
  /** value -> row count (§16.3). A missing key renders with no count suffix; a present 0 renders
   * dimmed, never hidden or disabled -- it's still reachable from a shared/typed URL. */
  facetCounts?: Record<string, number>;
  /** The "(has no value)" row. Omitted entirely (no row rendered) when the caller has no such
   * concept -- e.g. a plain multi-select picker, not a filter. */
  noValueOption?: { label: string; checked: boolean; count?: number; onChange: (checked: boolean) => void };
  /** The "Exclude these" toggle. Omitted entirely when the caller has no exclude concept. */
  excludeToggle?: { label: string; checked: boolean; onChange: (checked: boolean) => void };
  searchPlaceholder?: string;
  /** The fixed words, translated by the caller (fleet copy, 2026-11-02). Each defaults to English. */
  labels?: { selectAll?: string; clear?: string; noMatches?: string; valueSuffix?: string; searchSuffix?: string };
  /** Called on Enter while focus is anywhere inside this control. Omitted for a picker with no
   * separate "apply" step (selection commits immediately on every click). */
  onEnter?: () => void;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    width: '100%',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  listActions: {
    display: 'flex',
    gap: '8px',
  },
  listWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    maxHeight: '220px',
    overflowY: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    padding: '4px',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  noValueRow: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: '2px',
    paddingTop: '4px',
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    padding: '4px',
  },
});

/** `label (count)`, or just `label` when no count is known (§16.3: "not provided" ≠ "computed as zero"). */
function withCount(label: string, count: number | undefined): string {
  return count === undefined ? label : `${label} (${count})`;
}

export function SearchableMultiSelect({
  label,
  options,
  selected,
  onChange,
  facetCounts,
  noValueOption,
  labels,
  excludeToggle,
  searchPlaceholder,
  onEnter,
}: SearchableMultiSelectProps) {
  const styles = useStyles();
  const rootRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');

  const visibleOptions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => o.label.toLowerCase().includes(needle));
  }, [options, search]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  function toggle(value: string) {
    if (selectedSet.has(value)) onChange(selected.filter((v) => v !== value));
    else onChange([...selected, value]);
  }

  function selectAllVisible() {
    const merged = new Set(selected);
    for (const o of visibleOptions) merged.add(o.value);
    onChange(Array.from(merged));
  }

  function clearList() {
    onChange([]);
    noValueOption?.onChange(false);
  }

  function moveFocus(delta: 1 | -1) {
    const root = rootRef.current;
    if (!root) return;
    const focusables = Array.from(root.querySelectorAll<HTMLElement>('input[type="checkbox"]'));
    if (focusables.length === 0) return;
    const active = document.activeElement as HTMLElement | null;
    const idx = active ? focusables.indexOf(active) : -1;
    if (idx === -1) {
      focusables[delta === 1 ? 0 : focusables.length - 1]?.focus();
      return;
    }
    const next = Math.min(Math.max(idx + delta, 0), focusables.length - 1);
    focusables[next]?.focus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveFocus(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveFocus(-1);
    } else if (e.key === 'Enter' && onEnter) {
      e.preventDefault();
      onEnter();
    }
    // Escape is deliberately NOT handled here -- it bubbles to the panel's own dismiss().
  }

  const chips: { key: string; text: string; onRemove: () => void }[] = [
    ...selected.map((v) => ({
      key: v,
      text: options.find((o) => o.value === v)?.label ?? v,
      onRemove: () => toggle(v),
    })),
    ...(noValueOption?.checked ? [{ key: '__novalue__', text: noValueOption.label, onRemove: () => noValueOption.onChange(false) }] : []),
  ];

  return (
    <div
      ref={rootRef}
      className={styles.root}
      role="group"
      aria-label={`${label} ${labels?.valueSuffix ?? 'value' /* i18n-ignore: English default; callers pass labels */}`}
      onKeyDown={handleKeyDown}
    >
      {excludeToggle ? (
        <Switch
          label={excludeToggle.label}
          checked={excludeToggle.checked}
          onChange={(_, d) => excludeToggle.onChange(d.checked)}
        />
      ) : null}

      {chips.length > 0 ? (
        <div className={styles.chips} data-testid="searchable-multiselect-chips">
          {chips.map((chip) => (
            <Badge
              key={chip.key}
              shape="rounded"
              appearance="tint"
              icon={<Dismiss12Regular onClick={chip.onRemove} style={{ cursor: 'pointer' }} />}
              iconPosition="after"
            >
              {chip.text}
            </Badge>
          ))}
        </div>
      ) : null}

      <Field label={{ children: `${label} ${labels?.searchSuffix ?? 'search' /* i18n-ignore: English default; callers pass labels */}`, className: 'sr-only' }}>
        <Input
          size="small"
          value={search}
          placeholder={searchPlaceholder ?? 'Type to search...'}
          onChange={(_, d) => setSearch(d.value)}
        />
      </Field>

      <div className={styles.listActions}>
        <Button size="small" appearance="subtle" onClick={selectAllVisible} disabled={visibleOptions.length === 0}>
          {labels?.selectAll ?? 'Select all' /* i18n-ignore: English default; callers pass labels */}
        </Button>
        <Button size="small" appearance="subtle" onClick={clearList} disabled={selected.length === 0 && !noValueOption?.checked}>
          {labels?.clear ?? 'Clear' /* i18n-ignore: English default; callers pass labels */}
        </Button>
      </div>

      <div className={styles.listWrap}>
        {visibleOptions.length === 0 ? (
          <div className={styles.empty}>{labels?.noMatches ?? 'No matches' /* i18n-ignore: English default; callers pass labels */}</div>
        ) : (
          visibleOptions.map((o) => {
            const count = facetCounts?.[o.value];
            return (
              <div key={o.value} className={styles.option} style={count === 0 ? { opacity: 0.5 } : undefined}>
                <Checkbox
                  checked={selectedSet.has(o.value)}
                  onChange={() => toggle(o.value)}
                  label={withCount(o.label, count)}
                />
              </div>
            );
          })
        )}
        {noValueOption ? (
          <div className={styles.noValueRow} style={noValueOption.count === 0 ? { opacity: 0.5 } : undefined}>
            <Checkbox
              checked={noValueOption.checked}
              onChange={(_, d) => noValueOption.onChange(!!d.checked)}
              label={withCount(noValueOption.label, noValueOption.count)}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default SearchableMultiSelect;
